import React, { useState, useRef, useCallback } from "react";

const API = process.env.REACT_APP_API_URL;

// ─── Icons ────────────────────────────────────────────────────
const CheckIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2.5}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const SpinnerIcon = () => (
  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
);

const UploadIcon = () => (
  <svg
    className="w-8 h-8"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
    />
  </svg>
);

const FileIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
    />
  </svg>
);

// ─── OTP Input Component ──────────────────────────────────────
function OTPInput({ value, onChange, disabled }) {
  const inputs = useRef([]);
  const digits = value.padEnd(6, "").split("");

  const handleChange = (i, e) => {
    const val = e.target.value.replace(/\D/g, "");
    if (!val) return;
    const newDigits = [...digits];
    newDigits[i] = val[val.length - 1];
    onChange(newDigits.join("").slice(0, 6));
    if (i < 5 && val) inputs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      const newDigits = [...digits];
      if (newDigits[i]) {
        newDigits[i] = "";
        onChange(newDigits.join(""));
      } else if (i > 0) {
        newDigits[i - 1] = "";
        onChange(newDigits.join(""));
        inputs.current[i - 1]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (pasted) {
      onChange(pasted.padEnd(6, "").slice(0, 6));
      inputs.current[Math.min(pasted.length, 5)]?.focus();
    }
    e.preventDefault();
  };

  return (
    <div className="flex gap-2 justify-center">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i] || ""}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className={`
            w-11 h-14 text-center font-mono font-bold text-xl rounded-xl border
            bg-white text-slate-800 outline-none
            transition-all duration-150
            ${digits[i] ? "border-indigo-500 bg-indigo-50" : "border-slate-200"}
            focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/30
            disabled:opacity-40
          `}
        />
      ))}
    </div>
  );
}

// ─── Step Indicator ───────────────────────────────────────────
function StepIndicator({ currentStep }) {
  const steps = ["Details", "Verify Email", "Upload & Submit"];
  return (
    <div className="flex items-center gap-0 mb-10">
      {steps.map((label, i) => {
        const step = i + 1;
        const done = currentStep > step;
        const active = currentStep === step;
        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <div
                className={`
                w-8 h-8 rounded-full flex items-center justify-center text-xs font-display font-bold
                transition-all duration-300
                ${
                  done
                    ? "bg-indigo-500 text-white shadow-lg shadow-indigo-500/30"
                    : active
                      ? "bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-lg shadow-violet-500/30 scale-110"
                      : "bg-slate-100 text-slate-400 border border-slate-200"
                }
              `}
              >
                {done ? <CheckIcon /> : step}
              </div>
              <span
                className={`text-[10px] font-display font-semibold tracking-wider uppercase whitespace-nowrap
                ${active ? "text-indigo-500" : done ? "text-slate-600" : "text-slate-400"}`}
              >
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`flex-1 h-px mx-2 mb-4 transition-all duration-500
                ${currentStep > step ? "bg-indigo-500" : "bg-slate-200"}`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────
function Toast({ msg, type }) {
  if (!msg) return null;
  return (
    <div
      className={`
      flex items-start gap-3 px-4 py-3 rounded-xl text-sm font-body
      animate-fade-up border
      ${
        type === "error"
          ? "bg-red-50 border-red-200 text-red-500"
          : "bg-emerald-50 border-emerald-200 text-emerald-600"
      }
    `}
    >
      <span className="mt-0.5">{type === "error" ? "✕" : "✓"}</span>
      <span>{msg}</span>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────
export default function App() {
  const [step, setStep] = useState(1);
  const [toast, setToast] = useState({ msg: "", type: "" });

  // Form state
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    passoutYear: "",
    resume: null,
  });
  const [errors, setErrors] = useState({});

  // OTP state
  const [otp, setOtp] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);

  // Resume drag state
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef();

  const showToast = (msg, type = "error") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "" }), 4000);
  };

  const startCooldown = () => {
    setOtpCooldown(60);
    const t = setInterval(() => {
      setOtpCooldown((c) => {
        if (c <= 1) {
          clearInterval(t);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  // ── Validation Step 1 ──
  const validateStep1 = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.phone.trim()) e.phone = "Phone is required";
    else if (!/^\+?[\d\s\-()]{7,15}$/.test(form.phone))
      e.phone = "Enter a valid phone number";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email address";
    if (!form.passoutYear) e.passoutYear = "Passout year is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: "" }));
    if (field === "email") {
      setOtpSent(false);
      setOtpVerified(false);
      setOtp("");
    }
  };

  // ── Step 1 → 2 ──
  const handleNext = () => {
    if (validateStep1()) setStep(2);
  };

  // ── Send OTP ──
  const handleSendOTP = async () => {
    setOtpSending(true);
    try {
      const res = await fetch(`${API}/api/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email }),
      });
      const data = await res.json();
      if (data.success) {
        setOtpSent(true);
        setOtp("");
        showToast("OTP sent to your email!", "success");
        startCooldown();
      } else {
        showToast(data.message || "Failed to send OTP");
      }
    } catch {
      showToast("Network error. Check if backend is running.");
    } finally {
      setOtpSending(false);
    }
  };

  // ── Verify OTP ──
  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      showToast("Please enter all 6 digits");
      return;
    }
    setOtpVerifying(true);
    try {
      const res = await fetch(`${API}/api/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, otp }),
      });
      const data = await res.json();
      if (data.success) {
        setOtpVerified(true);
        showToast("Email verified successfully!", "success");
        setTimeout(() => setStep(3), 800);
      } else {
        showToast(data.message || "Invalid OTP");
      }
    } catch {
      showToast("Network error. Check if backend is running.");
    } finally {
      setOtpVerifying(false);
    }
  };

  // ── File Handling ──
  const handleFile = useCallback(
    (file) => {
      if (!file) return;
      const allowed = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowed.includes(file.type)) {
        showToast("Only PDF and Word (.doc/.docx) files are allowed");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showToast("File size must be under 5MB");
        return;
      }
      setForm((f) => ({ ...f, resume: file }));
      if (errors.resume) setErrors((e) => ({ ...e, resume: "" }));
    },
    [errors.resume],
  );

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!form.resume) {
      showToast("Please upload your resume");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      fd.append("email", form.email);
      fd.append("passoutYear", form.passoutYear);
      fd.append("resume", form.resume);

      const res = await fetch(`${API}/api/apply`, { method: "POST", body: fd });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        showToast(data.message || "Submission failed. Please try again.");
      }
    } catch {
      showToast("Network error. Check if backend is running.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatBytes = (b) =>
    b < 1024 * 1024
      ? `${(b / 1024).toFixed(1)} KB`
      : `${(b / (1024 * 1024)).toFixed(1)} MB`;

  // ─── Success Screen ──────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center animate-fade-up">
          <div
            className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500
                          flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-violet-500/20"
          >
            <svg
              className="w-10 h-10 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="font-display font-bold text-3xl text-slate-800 mb-3">
            Application Submitted!
          </h2>
          <p className="text-slate-600 font-body text-base leading-relaxed mb-2">
            Thank you for applying,{" "}
            <span className="text-slate-800 font-semibold">{form.name}</span>!
          </p>
          <p className="text-slate-500 font-body text-sm leading-relaxed mb-8">
            We got your profile — we will reach you soon.
            <br />
            Check your email for a confirmation.
          </p>
          <a
            href="https://vblptechsolutions.com/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl
                       bg-gradient-to-r from-indigo-500 to-violet-500
                       text-white font-display font-bold text-sm tracking-wide
                       hover:from-indigo-400 hover:to-violet-400 transition-all duration-200
                       shadow-lg shadow-indigo-500/20"
          >
            Visit VBLP Tech Solutions
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
              />
            </svg>
          </a>
        </div>
      </div>
    );
  }

  // ─── Main Form ───────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-indigo-400/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-violet-400/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-lg relative z-10">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-up">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full
                          bg-indigo-50 border border-indigo-200 mb-5"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
            <span className="text-indigo-600 text-xs font-display font-semibold tracking-widest uppercase">
              VBLP Tech Solutions
            </span>
          </div>
          <h1 className="font-display font-extrabold text-3xl text-slate-800 tracking-tight mb-2">
            Join Our Team
          </h1>
          <p className="text-slate-500 text-sm font-body">
            Apply now — we'll reach out to the best fits
          </p>
        </div>

        {/* Card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-7 shadow-xl shadow-slate-200/80">
          <StepIndicator currentStep={step} />

          {/* ── STEP 1: Details ── */}
          {step === 1 && (
            <div className="space-y-5 animate-fade-up">
              {/* Name */}
              <div>
                <label className="field-label">Full Name</label>
                <input
                  type="text"
                  className={`field-input ${errors.name ? "border-red-300 focus:border-red-400" : ""}`}
                  placeholder="e.g. Anika Sharma"
                  value={form.name}
                  onChange={(e) => handleField("name", e.target.value)}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1.5 font-body">
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="field-label">Phone Number</label>
                <input
                  type="tel"
                  className={`field-input ${errors.phone ? "border-red-300 focus:border-red-400" : ""}`}
                  placeholder="e.g. +91 98765 43210"
                  value={form.phone}
                  onChange={(e) => handleField("phone", e.target.value)}
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1.5 font-body">
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="field-label">Email Address</label>
                <input
                  type="email"
                  className={`field-input ${errors.email ? "border-red-300 focus:border-red-400" : ""}`}
                  placeholder="e.g. anika@example.com"
                  value={form.email}
                  onChange={(e) => handleField("email", e.target.value)}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1.5 font-body">
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Passout Year */}
              <div>
                <label className="field-label">Passout Year</label>
                <select
                  className={`field-input ${errors.passoutYear ? "border-red-300" : ""}`}
                  value={form.passoutYear}
                  onChange={(e) => handleField("passoutYear", e.target.value)}
                >
                  <option value="">Select year</option>
                  {[2022, 2023, 2024, 2025, 2026].map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                {errors.passoutYear && (
                  <p className="text-red-500 text-xs mt-1.5 font-body">
                    {errors.passoutYear}
                  </p>
                )}
              </div>

              <Toast msg={toast.msg} type={toast.type} />

              <button className="btn-primary mt-2" onClick={handleNext}>
                Continue to Email Verification →
              </button>
            </div>
          )}

          {/* ── STEP 2: OTP ── */}
          {step === 2 && (
            <div className="space-y-6 animate-fade-up">
              <div className="text-center">
                <div
                  className="w-14 h-14 rounded-2xl bg-indigo-50 border border-indigo-100
                                flex items-center justify-center mx-auto mb-4"
                >
                  <svg
                    className="w-7 h-7 text-indigo-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                    />
                  </svg>
                </div>
                <h3 className="font-display font-bold text-lg text-slate-800 mb-1">
                  Verify Your Email
                </h3>
                <p className="text-slate-500 text-sm font-body">
                  We'll send a 6-digit OTP to
                  <br />
                  <span className="text-indigo-500 font-semibold">
                    {form.email}
                  </span>
                </p>
              </div>

              {!otpVerified && (
                <>
                  <button
                    className="btn-secondary w-full flex items-center justify-center gap-2"
                    onClick={handleSendOTP}
                    disabled={otpSending || otpCooldown > 0}
                  >
                    {otpSending ? (
                      <>
                        <SpinnerIcon /> Sending OTP...
                      </>
                    ) : otpCooldown > 0 ? (
                      `Resend in ${otpCooldown}s`
                    ) : otpSent ? (
                      "Resend OTP"
                    ) : (
                      "Send OTP to Email"
                    )}
                  </button>

                  {otpSent && (
                    <div className="space-y-4">
                      <div>
                        <label className="field-label text-center block mb-4">
                          Enter 6-digit OTP
                        </label>
                        <OTPInput
                          value={otp}
                          onChange={setOtp}
                          disabled={otpVerifying}
                        />
                      </div>
                      <button
                        className="btn-primary flex items-center justify-center gap-2"
                        onClick={handleVerifyOTP}
                        disabled={otpVerifying || otp.length < 6}
                      >
                        {otpVerifying ? (
                          <>
                            <SpinnerIcon /> Verifying...
                          </>
                        ) : (
                          "Verify OTP"
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}

              {otpVerified && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 text-white">
                    <CheckIcon />
                  </div>
                  <div>
                    <p className="text-emerald-700 font-display font-semibold text-sm">
                      Email Verified!
                    </p>
                    <p className="text-emerald-500 text-xs font-body">
                      Redirecting to next step…
                    </p>
                  </div>
                </div>
              )}

              <Toast msg={toast.msg} type={toast.type} />

              <button
                className="btn-secondary w-full text-center"
                onClick={() => setStep(1)}
              >
                ← Back to Details
              </button>
            </div>
          )}

          {/* ── STEP 3: Resume & Submit ── */}
          {step === 3 && (
            <div className="space-y-6 animate-fade-up">
              <div>
                <label className="field-label">Upload Resume</label>
                <div
                  className={`
                    relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer
                    transition-all duration-200
                    ${
                      dragging
                        ? "border-indigo-400 bg-indigo-50"
                        : form.resume
                          ? "border-emerald-400 bg-emerald-50/50"
                          : "border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50"
                    }
                  `}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files[0])}
                  />
                  {form.resume ? (
                    <div className="flex items-center justify-center gap-3">
                      <div
                        className="w-10 h-10 rounded-xl bg-emerald-100 border border-emerald-200
                                      flex items-center justify-center text-emerald-600 flex-shrink-0"
                      >
                        <FileIcon />
                      </div>
                      <div className="text-left min-w-0">
                        <p className="text-slate-800 text-sm font-display font-semibold truncate max-w-[200px]">
                          {form.resume.name}
                        </p>
                        <p className="text-emerald-600 text-xs font-body">
                          {formatBytes(form.resume.size)}
                        </p>
                      </div>
                      <button
                        className="ml-auto text-slate-400 hover:text-red-400 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          setForm((f) => ({ ...f, resume: null }));
                        }}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div className="text-slate-400 mb-3 flex justify-center">
                        <UploadIcon />
                      </div>
                      <p className="text-slate-500 text-sm font-body mb-1">
                        Drag & drop or{" "}
                        <span className="text-indigo-500 font-semibold">
                          browse
                        </span>
                      </p>
                      <p className="text-slate-400 text-xs font-body">
                        PDF, DOC, DOCX · Max 5MB
                      </p>
                    </div>
                  )}
                </div>
                {errors.resume && (
                  <p className="text-red-500 text-xs mt-1.5 font-body">
                    {errors.resume}
                  </p>
                )}
              </div>

              {/* Summary */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                <p className="text-xs font-display font-semibold tracking-widest text-slate-400 uppercase mb-3">
                  Application Summary
                </p>
                {[
                  { label: "Name", value: form.name },
                  { label: "Phone", value: form.phone },
                  { label: "Email", value: form.email },
                  { label: "Passout Year", value: form.passoutYear },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between text-sm">
                    <span className="text-slate-400 font-body">{label}</span>
                    <span className="text-slate-700 font-body">{value}</span>
                  </div>
                ))}
              </div>

              <Toast msg={toast.msg} type={toast.type} />

              <button
                className="btn-primary flex items-center justify-center gap-2"
                onClick={handleSubmit}
                disabled={submitting || !form.resume}
              >
                {submitting ? (
                  <>
                    <SpinnerIcon /> Submitting Application…
                  </>
                ) : (
                  "Submit Application →"
                )}
              </button>

              <button
                className="btn-secondary w-full text-center"
                onClick={() => setStep(2)}
              >
                ← Back
              </button>
            </div>
          )}
        </div>

        {/* Footer link */}
        <p className="text-center text-slate-400 text-xs font-body mt-6">
          <a
            href="https://vblptechsolutions.com/"
            target="_blank"
            rel="noreferrer"
            className="text-indigo-400 hover:text-indigo-500 transition-colors"
          >
            vblptechsolutions.com
          </a>
        </p>
      </div>
    </div>
  );
}
